import { useAppStore } from '../store';
import { motion } from 'framer-motion';
import { TrendingUp, Target, AlertCircle, BookOpen, Brain, Star, ChevronRight, Zap, BarChart2 } from 'lucide-react';
import StatsCharts from '../components/StatsCharts';

function ProgressRing({ value, size = 80, stroke = 5, color = 'var(--sage)' }: { value: number; size?: number; stroke?: number; color?: string }) {
  const radius = (size - stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="rgba(28,25,23,0.05)" strokeWidth={stroke} fill="none" />
      <motion.circle
        cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth={stroke} fill="none"
        strokeLinecap="round" strokeDasharray={circumference}
        initial={{ strokeDashoffset: circumference }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      />
    </svg>
  );
}

function KnowledgeGraph() {
  const { units, activeSubjectId } = useAppStore();
  const subjectUnits = units.filter(u => u.subjectId === activeSubjectId);
  const allPoints = subjectUnits.flatMap(u => u.knowledgePoints);

  const positions = allPoints.map((_, i) => {
    const cols = 5;
    const row = Math.floor(i / cols);
    const col = i % cols;
    const jitter = (Math.sin(i * 3.7) * 15);
    return { x: 80 + col * 130 + jitter, y: 60 + row * 100 + jitter * 0.5 };
  });

  return (
    <div className="card-premium relative p-4 sm:p-5 overflow-hidden" style={{ minHeight: 280 }}>
      <h3 className="text-base font-display mb-4 flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-lg bg-sage/10 flex items-center justify-center">
          <Brain className="w-4 h-4 text-sage" />
        </div>
        互动知识图谱
      </h3>
      <div className="overflow-x-auto scroll-x-mobile -mx-4 sm:-mx-5 px-4 sm:px-5">
        <svg width="700" height="260" viewBox="0 0 700 260" className="min-w-[500px]">
          {allPoints.map((point, i) =>
            point.connections.map(connId => {
              const j = allPoints.findIndex(p => p.id === connId);
              if (j === -1 || j <= i) return null;
              return (
                <motion.line
                  key={`${point.id}-${connId}`}
                  x1={positions[i].x} y1={positions[i].y}
                  x2={positions[j].x} y2={positions[j].y}
                  stroke="rgba(28,25,23,0.06)" strokeWidth={1.5}
                  initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                  transition={{ duration: 0.8, delay: i * 0.1 }}
                />
              );
            })
          )}
          {allPoints.map((point, i) => {
            const pos = positions[i];
            const color = point.mastery > 70 ? 'var(--sage)' : point.mastery > 40 ? 'var(--bronze)' : 'var(--terracotta)';
            const size = point.isExamFocus ? 22 : 16;
            return (
              <g key={point.id} className="knowledge-node cursor-pointer">
                <motion.circle
                  cx={pos.x} cy={pos.y} r={size}
                  fill={color} fillOpacity={0.12} stroke={color} strokeWidth={1.5}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                />
                <motion.circle cx={pos.x} cy={pos.y} r={3.5} fill={color}
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ duration: 0.3, delay: i * 0.08 + 0.2 }}
                />
                <text x={pos.x} y={pos.y + size + 14} textAnchor="middle" className="text-[9px] fill-stone-500 font-medium font-body">
                  {point.title.length > 8 ? point.title.slice(0, 8) + '…' : point.title}
                </text>
                <text x={pos.x} y={pos.y + size + 25} textAnchor="middle" className="text-[8px] fill-stone-400 font-body">
                  {point.mastery}%
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:gap-5 mt-3 text-[10px] text-stone-400 font-body">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sage" />掌握良好</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-bronze" />需要加强</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-terracotta" />薄弱环节</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border-[1.5px] border-stone-300" />IB 考点</span>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { units, activeSubjectId, progress, mistakes, flashcards, setActiveModule } = useAppStore();
  const subjectUnits = units.filter(u => u.subjectId === activeSubjectId);
  const currentProgress = progress.find(p => p.subjectId === activeSubjectId);
  const subjectMistakes = mistakes.filter(m => m.question.subjectId === activeSubjectId);
  const subjectFlashcards = flashcards.filter(f => f.subjectId === activeSubjectId);
  const unresolvedMistakes = subjectMistakes.filter(m => !m.resolved).length;
  const masteredCards = subjectFlashcards.filter(f => f.mastered).length;

  const statCards = [
    { label: '总体进度', value: `${currentProgress?.overallProgress || 0}%`, icon: TrendingUp, color: 'var(--sage)', bg: 'bg-sage-50' },
    { label: '预估等级', value: `${currentProgress?.predictedGrade || 0}/7`, icon: Star, color: 'var(--bronze)', bg: 'bg-cedar-50' },
    { label: '待解错题', value: unresolvedMistakes, icon: AlertCircle, color: 'var(--terracotta)', bg: 'bg-terracotta-50' },
    { label: '闪读卡进度', value: `${masteredCards}/${subjectFlashcards.length}`, icon: Zap, color: 'var(--steel)', bg: 'bg-steel-50' },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">
      {/* Hero section — editorial style */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display text-stone-950 tracking-tight">学习仪表盘</h1>
        <p className="text-sm text-stone-400 mt-1.5 sm:mt-2 font-body max-w-lg">追踪学习进度，掌握 IB 考核重点，发现你的成长轨迹</p>
      </motion.div>

      {/* Stat cards — bento style */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="card-premium p-4 sm:p-5"
            >
              <div className="flex items-start justify-between mb-2 sm:mb-3">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${card.bg} flex items-center justify-center`}>
                  <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" style={{ color: card.color }} />
                </div>
              </div>
              <p className="text-[10px] sm:text-[11px] text-stone-400 font-body">{card.label}</p>
              <p className="text-xl sm:text-2xl font-display text-stone-950 mt-0.5">{card.value}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Knowledge Graph + Unit Progress — asymmetric grid */}
      <div className="grid lg:grid-cols-5 gap-4 sm:gap-6">
        <motion.div
          className="lg:col-span-3"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <KnowledgeGraph />
        </motion.div>
        <div className="lg:col-span-2 space-y-4 sm:space-y-5">
          <motion.div
            className="card-premium p-4 sm:p-5"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <h3 className="text-base font-display mb-3 sm:mb-4 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-steel-50 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-steel" />
              </div>
              单元学习进度
            </h3>
            <div className="space-y-3 sm:space-y-3.5">
              {subjectUnits.map((unit, i) => (
                <motion.div
                  key={unit.id}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="relative flex-shrink-0">
                    <ProgressRing value={unit.progress} size={48} stroke={4} />
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-stone-950 font-body">
                      {unit.progress}%
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-stone-950 truncate font-body">{unit.title}</p>
                    <p className="text-[10px] text-stone-400 font-body">{unit.knowledgePoints.length} 个知识点</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Weak areas alert */}
          {currentProgress?.weakAreas && currentProgress.weakAreas.length > 0 && (
            <motion.div
              className="rounded-2xl p-4 border border-terracotta-light/30 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #fdf0ec, #fef6f0)' }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h3 className="text-sm font-semibold text-stone-800 mb-2.5 flex items-center gap-2 font-body">
                <Target className="w-4 h-4 text-terracotta" />
                薄弱环节提醒
              </h3>
              <div className="space-y-1.5">
                {currentProgress.weakAreas.map(area => (
                  <div key={area} className="flex items-center gap-2 text-xs text-stone-600 font-body">
                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta pulse-weak" />
                    {area}
                  </div>
                ))}
              </div>
              <button
                onClick={() => setActiveModule('difficulty')}
                className="mt-3 text-[11px] font-medium text-terracotta hover:text-stone-800 flex items-center gap-1 transition-colors font-body"
              >
                前往难点解答 <ChevronRight className="w-3 h-3" />
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Statistics Charts Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.5 }}
      >
        <div className="flex items-center gap-2.5 mb-4 sm:mb-5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-sage to-sage-dark flex items-center justify-center">
            <BarChart2 className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-display text-stone-950">学习数据统计</h2>
            <p className="text-[11px] text-stone-400">可视化你的学习进度与表现</p>
          </div>
        </div>
        <StatsCharts />
      </motion.div>

      {/* Quick actions — warm gradient cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: '上传资料', module: 'materials', desc: '上传教材或笔记', gradient: 'from-steel to-steel-light' },
          { label: '开始练习', module: 'exercises', desc: '分层能力训练', gradient: 'from-sage-dark to-sage' },
          { label: '模拟测试', module: 'tests', desc: 'IB 校内测验模拟', gradient: 'from-bronze-dark to-bronze' },
          { label: '复习闪读卡', module: 'flashcards', desc: '关键术语速记', gradient: 'from-cedar to-cedar-light' },
        ].map((action, i) => (
          <motion.button
            key={action.module}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + i * 0.08 }}
            onClick={() => setActiveModule(action.module)}
            className={`bg-gradient-to-br ${action.gradient} text-white rounded-2xl p-4 sm:p-5 text-left card-hover relative overflow-hidden group touch-target`}
          >
            <div className="absolute inset-0 bg-white/0 group-hover:bg-white/[0.06] transition-colors duration-300" />
            <p className="text-sm font-semibold relative z-10 font-body">{action.label}</p>
            <p className="text-[11px] text-white/60 mt-1 relative z-10 font-body">{action.desc}</p>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
